/**
 * AI Service
 *
 * API methods for AI-powered features: risk summaries, assistant, batch analysis.
 * All methods support abort signals for request cancellation.
 */

import { ApiClient } from './api';

export class AIService {
  constructor(getToken) {
    this.client = new ApiClient(getToken);
  }

  getRiskSummary(applicantId, options = {}) {
    return this.client.get(`/ai/applicants/${applicantId}/risk-summary`, options);
  }

  regenerateRiskSummary(applicantId, options = {}) {
    return this.client.post(`/ai/applicants/${applicantId}/risk-summary`, {}, options);
  }

  // AI Analyst agent — first-level KYC review (recommend-only).
  // Pass `recipe` (name) to run a specific review recipe; omit for the default/full checklist.
  runAnalystReview(applicantId, recipe = null, options = {}) {
    const qs = recipe ? `?recipe=${encodeURIComponent(recipe)}` : '';
    return this.client.post(`/ai/applicants/${applicantId}/analyst-review${qs}`, {}, options);
  }

  // Review recipes — reusable, tenant-scoped, named review definitions.
  listReviewRecipes(options = {}) {
    return this.client.get('/ai/agent/recipes', options);
  }

  createReviewRecipe(payload, options = {}) {
    // payload: { name, sections?, custom_items?, description?, set_default? }
    return this.client.post('/ai/agent/recipes', payload, options);
  }

  // AI Analyst agent — review all outstanding applicants
  reviewOutstanding(limit = 10, options = {}) {
    return this.client.post(`/ai/agent/review-outstanding?limit=${limit}`, {}, options);
  }

  // Conversational agent ("Megan") — send message history, get her reply
  chatWithAgent(messages, options = {}) {
    return this.client.post('/ai/agent/chat', { messages }, options);
  }

  // Most-recent thread — rehydrates the UI on load.
  // Returns { thread_id, title, messages: [{ role, content, toolEvents }] }.
  getChatHistory(options = {}) {
    return this.client.get('/ai/agent/history', options);
  }

  // Sidebar list of chat threads, most-recent first.
  // Returns { threads: [{ id, title, last_message_at, created_at, message_count }] }.
  getThreads(options = {}) {
    return this.client.get('/ai/agent/threads', options);
  }

  // Load one thread's messages. Returns { id, title, messages: [...] }.
  getThread(threadId, options = {}) {
    return this.client.get(`/ai/agent/threads/${threadId}`, options);
  }

  // Delete one chat thread.
  deleteThread(threadId, options = {}) {
    return this.client.delete(`/ai/agent/threads/${threadId}`, options);
  }

  // Streaming chat (SSE): onEvent gets {type:'tool'|'text'|'done'|'error', ...}
  // as Megan works, so the UI can show live tool activity + streamed text.
  async streamChat(messages, { onEvent, signal, threadId } = {}) {
    const token = await this.client.getToken();
    const res = await fetch(`${this.client.baseUrl}/ai/agent/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ messages, thread_id: threadId ?? null }),
      signal,
    });
    if (!res.ok || !res.body) {
      throw new Error(`Chat failed (${res.status})`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf('\n\n')) >= 0) {
        const frame = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const line = frame.split('\n').find((l) => l.startsWith('data:'));
        if (!line) continue;
        try {
          onEvent?.(JSON.parse(line.slice(5).trim()));
        } catch {
          /* ignore malformed frame */
        }
      }
    }
  }

  // AI Analyst agent — investigate adverse-media hits (same person? true match?)
  investigateAdverseMedia(applicantId, options = {}) {
    return this.client.post(`/ai/applicants/${applicantId}/investigate-adverse-media`, {}, options);
  }

  askAssistant(query, applicantId = null, context = null, options = {}) {
    const body = { query };
    if (applicantId) body.applicant_id = applicantId;
    if (context) body.context = context;
    return this.client.post('/ai/assistant', body, options);
  }

  batchAnalyze(applicantIds, options = {}) {
    return this.client.post('/ai/batch-analyze', { applicant_ids: applicantIds }, options);
  }

  getBatchStatus(jobId, options = {}) {
    return this.client.get(`/ai/batch-analyze/${jobId}`, options);
  }

  getDocumentSuggestions(documentId, options = {}) {
    return this.client.get(`/ai/documents/${documentId}/suggestions`, options);
  }

  // Company risk narrative (KYB)
  getCompanyRiskNarrative(companyId, options = {}) {
    return this.client.get(`/ai/companies/${companyId}/risk-narrative`, options);
  }

  regenerateCompanyRiskNarrative(companyId, options = {}) {
    return this.client.post(`/ai/companies/${companyId}/risk-narrative`, {}, options);
  }
}

export default AIService;
